import { HttpInterceptorFn, HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { of, throwError } from 'rxjs';
import { BackendFormConfig } from '../../shared/models/backend-form-config.model';

// ---------------------------------------------------------------------------
// Mock fixtures — typed against the frozen Raw* contract (.ai/api-contracts.md,
// DL-029/DL-031), not the frontend camelCase model. Keeps the mock and the real
// mapper (RecruitmentApiService.fromBackendVacancy) honest against the same shape.
// ---------------------------------------------------------------------------

interface RawInterviewStep {
  id: string;
  name: string;
  type: 'TECHNICAL' | 'HR' | 'MANAGEMENT' | 'EXAMINATION' | 'ATS';
  sequence_order: number;
}

interface RawVacancy {
  id: string;
  name: string;
  start_date: string;
  end_date: string;
  org_node_id: string;
  org_node_name?: string;
  job_title: string;
  job_description: string;
  salary_min: number;
  salary_max: number;
  salary_currency: string;
  location: string;
  employment_type: 'FULL_TIME' | 'PART_TIME' | 'CONTRACT';
  working_mode: 'ONSITE' | 'WFH' | 'HYBRID';
  number_of_openings: number;
  recruiter_id: string;
  recruiter_name?: string;
  external_visibility: boolean;
  interview_steps: RawInterviewStep[];
  status: 'SCHEDULED' | 'ACTIVE' | 'CLOSED';
  closed_reason?: 'MANUAL' | 'AUTO_FILLED' | null;
  positions_filled: number;
  created_at: string;
}

let MOCK_VACANCIES: RawVacancy[] = [
  {
    id: 'vac_001',
    name: 'Senior Backend Engineer',
    start_date: '2026-07-01',
    end_date: '2026-08-15',
    org_node_id: 'C001',
    org_node_name: 'RWB CA',
    job_title: 'Senior Backend Engineer',
    job_description: 'Design and build backend services for the HRMS platform.',
    salary_min: 250000,
    salary_max: 350000,
    salary_currency: 'LKR',
    location: 'Colombo, Sri Lanka',
    employment_type: 'FULL_TIME',
    working_mode: 'HYBRID',
    number_of_openings: 2,
    recruiter_id: '1',
    recruiter_name: 'Amal Bandara Perera',
    external_visibility: true,
    interview_steps: [
      { id: 'step_001', name: 'Technical Round 1', type: 'EXAMINATION', sequence_order: 1 },
      { id: 'step_002', name: 'HR Round', type: 'HR', sequence_order: 2 }
    ],
    status: 'ACTIVE',
    closed_reason: null,
    positions_filled: 0,
    created_at: '2026-06-20T09:00:00Z'
  },
  {
    id: 'vac_002',
    name: 'QA Automation Engineer',
    start_date: '2026-07-10',
    end_date: '2026-09-01',
    org_node_id: 'C002',
    org_node_name: 'RWB International',
    job_title: 'QA Automation Engineer',
    job_description: 'Own automated test coverage across the platform.',
    salary_min: 180000,
    salary_max: 240000,
    salary_currency: 'GBP',
    location: 'Remote',
    employment_type: 'CONTRACT',
    working_mode: 'WFH',
    number_of_openings: 1,
    recruiter_id: '1',
    recruiter_name: 'Amal Bandara Perera',
    external_visibility: false,
    interview_steps: [
      { id: 'step_003', name: 'HR Interview', type: 'HR', sequence_order: 1 },
      { id: 'step_004', name: 'Technical Round', type: 'TECHNICAL', sequence_order: 2 }
    ],
    status: 'SCHEDULED',
    closed_reason: null,
    positions_filled: 0,
    created_at: '2026-06-21T11:30:00Z'
  },
  {
    id: 'vac_003',
    name: 'Office Administrator',
    start_date: '2026-05-01',
    end_date: '2026-06-01',
    org_node_id: 'C003',
    org_node_name: 'Online Accounting',
    job_title: 'Office Administrator',
    job_description: 'Front-desk and office operations support.',
    salary_min: 90000,
    salary_max: 120000,
    salary_currency: 'LKR',
    location: 'Colombo, Sri Lanka',
    employment_type: 'FULL_TIME',
    working_mode: 'ONSITE',
    number_of_openings: 1,
    recruiter_id: '1',
    recruiter_name: 'Amal Bandara Perera',
    external_visibility: true,
    interview_steps: [
      { id: 'step_005', name: 'HR Round', type: 'HR', sequence_order: 1 }
    ],
    status: 'CLOSED',
    closed_reason: 'MANUAL',
    positions_filled: 1,
    created_at: '2026-04-15T08:00:00Z'
  }
];

// ---------------------------------------------------------------------------
// Candidates (Talent Pool) — fully connected to the real backend now (create/list/
// get/update + the vacancy_id query-param linking flow); nothing left to mock here.
// ---------------------------------------------------------------------------

// One flat step, two sections — runs through the real dynamic form pipeline
// (FormConfigAdapterService → DynamicFormService) like Employee Registration,
// just without the multi-step wizard UI. Field ids are snake_case matching
// RawCandidate exactly, so the form's raw value posts straight through with
// no separate camelCase→snake_case mapper needed for writes.
const MOCK_CANDIDATE_REGISTRATION_CONFIG: BackendFormConfig = {
  form_metadata: {
    form_id: 'recruitment_candidate_registration',
    form_name: 'Candidate Registration',
    settings: {
      submit_url: '/recruitment/candidates',
      layout: 'tabs'
    }
  },
  steps: [
    {
      step_order: 1,
      enabled: true,
      step_title: 'Candidate Details',
      sections: [
        {
          section_title: 'Personal Information',
          fields: [
            { id: 'name', type: 'text', label: 'Name', placeholder: 'e.g. Nimal Perera', width: 'full', validation: { required: true } },
            { id: 'email', type: 'email', label: 'Email', placeholder: 'e.g. nimal.perera@example.com', width: 'half', validation: { required: true, email: true } },
            { id: 'phone', type: 'text', label: 'Phone (with country code)', placeholder: 'e.g. +94771234567', width: 'half', validation: { required: true } },
            { id: 'address', type: 'textarea', label: 'Address', placeholder: 'e.g. No. 12, Galle Road, Colombo 03', width: 'full', validation: { required: true } }
          ]
        },
        {
          section_title: 'Compensation & Experience',
          fields: [
            { id: 'experience_years', type: 'number', label: 'Experience (years)', placeholder: 'e.g. 5', width: 'half' },
            { id: 'education', type: 'text', label: 'Education', placeholder: 'e.g. BSc (Hons) Computer Science', width: 'half' },
            { id: 'notice_period_days', type: 'number', label: 'Notice Period (days)', placeholder: 'e.g. 30', width: 'half' },
            { id: 'skills', type: 'text', label: 'Skills (comma-separated)', placeholder: 'e.g. Java, Spring Boot, PostgreSQL', width: 'half' },
            { id: 'current_salary_net', type: 'number', label: 'Current Salary (Net)', placeholder: 'e.g. 150000', width: 'half' },
            { id: 'current_salary_gross', type: 'number', label: 'Current Salary (Gross)', placeholder: 'e.g. 180000', width: 'half' },
            {
              id: 'current_salary_currency', type: 'select', label: 'Current Salary Currency', placeholder: 'Select currency', width: 'half',
              options_source: 'static',
              options: [{ value: 'LKR', label: 'LKR' }, { value: 'USD', label: 'USD' }, { value: 'GBP', label: 'GBP' }]
            },
            { id: 'expected_salary_net', type: 'number', label: 'Expected Salary (Net)', placeholder: 'e.g. 200000', width: 'half' },
            { id: 'expected_salary_gross', type: 'number', label: 'Expected Salary (Gross)', placeholder: 'e.g. 240000', width: 'half' },
            {
              id: 'expected_salary_currency', type: 'select', label: 'Expected Salary Currency', placeholder: 'Select currency', width: 'half',
              options_source: 'static',
              options: [{ value: 'LKR', label: 'LKR' }, { value: 'USD', label: 'USD' }, { value: 'GBP', label: 'GBP' }]
            },
            {
              id: 'cv_file',
              type: 'file_upload',
              label: 'Curriculum Vitae (CV)',
              placeholder: 'Upload CV',
              width: 'full',
              accept: '.pdf,.doc,.docx',
              validation: {
                allowed_extensions: ['.pdf', '.doc', '.docx']
              }
            }
          ]
        }
      ]
    }
  ]
};

let MOCK_CANDIDATES: any[] = [
  {
    id: 'cand_001',
    name: 'Nimal Perera',
    email: 'nimal.perera@example.com',
    phone: '+94771234567',
    address: '123 Galle Road, Colombo 03',
    notice_period_days: 30,
    current_salary_net: 180000,
    current_salary_gross: 200000,
    current_salary_currency: 'LKR',
    expected_salary_net: 240000,
    expected_salary_gross: 270000,
    expected_salary_currency: 'LKR',
    experience_years: 5,
    education: 'BSc in Computer Science',
    skills: ['TypeScript', 'Angular', 'Node.js'],
    cv_url: 'https://example.com/nimal-cv.pdf',
    cv_name: 'nimal-cv.pdf',
    created_at: '2026-06-20T10:00:00Z',
    vacancy_id: 'vac_001',
    approval_status: 'APPROVED',
    current_step_id: 'step_001'
  },
  {
    id: 'cand_002',
    name: 'Kasun Silva',
    email: 'kasun.silva@example.com',
    phone: '+94773456789',
    address: '45 Kandy Road, Kiribathgoda',
    notice_period_days: 15,
    current_salary_net: 140000,
    current_salary_gross: 160000,
    current_salary_currency: 'LKR',
    expected_salary_net: 200000,
    expected_salary_gross: 220000,
    expected_salary_currency: 'LKR',
    experience_years: 3,
    education: 'BSc in Software Engineering',
    skills: ['Java', 'Spring Boot', 'SQL'],
    cv_url: 'https://example.com/kasun-cv.pdf',
    cv_name: 'kasun-cv.pdf',
    created_at: '2026-06-22T09:00:00Z',
    vacancy_id: 'vac_001',
    approval_status: 'APPLIED',
    current_step_id: null
  }
];

// ---------------------------------------------------------------------------
// Vacancy Applications
// ---------------------------------------------------------------------------

interface RawStepDecisionRecord {
  step_id: string;
  action: 'ADVANCE' | 'REJECT';
  decided_at: string;
}

interface RawVacancyApplication {
  id: string;
  vacancy_id: string;
  candidate_id: string;
  candidate_name: string;
  candidate_email: string;
  candidate_phone: string;
  status: 'APPLIED' | 'SCREENING_APPROVED' | 'SCREENING_REJECTED' | 'REJECTED';
  current_step_id?: string | null;
  created_inline: boolean;
  applied_date: string;
  step_decisions: RawStepDecisionRecord[];
}

let MOCK_APPLICATIONS: RawVacancyApplication[] = [
  {
    id: 'app_001',
    vacancy_id: 'vac_001',
    candidate_id: 'cand_001',
    candidate_name: 'Nimal Perera',
    candidate_email: 'nimal.perera@example.com',
    candidate_phone: '+94771234567',
    status: 'SCREENING_APPROVED',
    current_step_id: 'step_001',
    created_inline: true,
    applied_date: '2026-06-20T10:00:00Z'.slice(0, 10),
    step_decisions: []
  },
  {
    id: 'app_002',
    vacancy_id: 'vac_001',
    candidate_id: 'cand_003',
    candidate_name: 'Kasun Silva',
    candidate_email: 'kasun.silva@example.com',
    candidate_phone: '+94773456789',
    status: 'APPLIED',
    current_step_id: null,
    created_inline: true,
    applied_date: '2026-06-22T09:00:00Z'.slice(0, 10),
    step_decisions: []
  }
];

// ---------------------------------------------------------------------------
// Question Bank
// ---------------------------------------------------------------------------

type RawQuestionType = 'MCQ_SINGLE' | 'MCQ_MULTI' | 'LONG_ANSWER' | 'RATING' | 'YES_NO';
type RawDifficultyLevel = 'EASY' | 'MEDIUM' | 'HARD';

interface RawQuestionOption {
  id: string;
  option_text: string;
  is_correct: boolean;
}

interface RawQuestion {
  id: string;
  subject: string;
  subject_id?: string;
  topic: string;
  topic_id?: string;
  question_text: string;
  status: 'ACTIVE' | 'INACTIVE';
  type: RawQuestionType;
  difficulty_level: RawDifficultyLevel;
  options?: RawQuestionOption[];
  model_answers?: string;
  created_at: string;
}

// Subject (criteria-categories) and Topic CRUD now hit the real exam backend — no mock fixtures
// for them remain here (see the removed-handlers note further down this file).

const MOCK_QUESTIONS: RawQuestion[] = [
  {
    id: 'quest_001', subject: 'Backend Development', subject_id: 'cat_001', topic: 'JavaScript', topic_id: 'topic_001', question_text: 'Which keyword declares a block-scoped variable in JavaScript?',
    status: 'ACTIVE', type: 'MCQ_SINGLE', difficulty_level: 'EASY',
    options: [
      { id: 'qopt_001', option_text: 'var', is_correct: false },
      { id: 'qopt_002', option_text: 'let', is_correct: true },
      { id: 'qopt_003', option_text: 'function', is_correct: false }
    ],
    created_at: '2026-06-20T08:00:00Z'
  },
  {
    id: 'quest_002', subject: 'Backend Development', subject_id: 'cat_001', topic: 'Node.js', topic_id: 'topic_002', question_text: 'Which of the following are valid ways to handle asynchronous code in Node.js? (select all that apply)',
    status: 'ACTIVE', type: 'MCQ_MULTI', difficulty_level: 'MEDIUM',
    options: [
      { id: 'qopt_004', option_text: 'Callbacks', is_correct: true },
      { id: 'qopt_005', option_text: 'Promises', is_correct: true },
      { id: 'qopt_006', option_text: 'async/await', is_correct: true },
      { id: 'qopt_007', option_text: 'Synchronous loops', is_correct: false }
    ],
    created_at: '2026-06-20T08:05:00Z'
  },
  {
    id: 'quest_003', subject: 'Backend Development', subject_id: 'cat_001', topic: 'Databases', topic_id: 'topic_003', question_text: 'Explain the difference between SQL and NoSQL databases, with an example use case for each.',
    status: 'ACTIVE', type: 'LONG_ANSWER', difficulty_level: 'MEDIUM',
    model_answers: 'SQL databases are relational and enforce a fixed schema (e.g. PostgreSQL for transactional order data); NoSQL databases are non-relational and schema-flexible (e.g. MongoDB for catalog data with varying attributes).',
    created_at: '2026-06-20T08:10:00Z'
  },
  {
    id: 'quest_004', subject: 'General Aptitude', subject_id: 'cat_002', topic: 'Logical Reasoning', topic_id: 'topic_005', question_text: 'Rate your confidence in solving algorithmic problems under time pressure.',
    status: 'ACTIVE', type: 'RATING', difficulty_level: 'EASY',
    created_at: '2026-06-20T08:15:00Z'
  },
  {
    id: 'quest_005', subject: 'General Aptitude', subject_id: 'cat_002', topic: 'Communication', topic_id: 'topic_006', question_text: 'Have you led a team or project before?',
    status: 'ACTIVE', type: 'YES_NO', difficulty_level: 'EASY',
    created_at: '2026-06-20T08:20:00Z'
  },
  {
    id: 'quest_006', subject: 'QA & Testing', subject_id: 'cat_003', topic: 'Test Design', topic_id: 'topic_007', question_text: 'Which testing technique is best suited for validating boundary values of an input range?',
    status: 'ACTIVE', type: 'MCQ_SINGLE', difficulty_level: 'MEDIUM',
    options: [
      { id: 'qopt_008', option_text: 'Boundary Value Analysis', is_correct: true },
      { id: 'qopt_009', option_text: 'Smoke Testing', is_correct: false },
      { id: 'qopt_010', option_text: 'Exploratory Testing', is_correct: false }
    ],
    created_at: '2026-06-20T08:25:00Z'
  },
  {
    id: 'quest_007', subject: 'QA & Testing', subject_id: 'cat_003', topic: 'Automation', topic_id: 'topic_008', question_text: 'Describe a scenario where you would prefer manual testing over automated testing.',
    status: 'ACTIVE', type: 'LONG_ANSWER', difficulty_level: 'HARD',
    model_answers: 'Exploratory or usability testing where human judgment on look-and-feel and unexpected behavior matters more than repeatable, scripted checks.',
    created_at: '2026-06-20T08:30:00Z'
  },
  {
    id: 'quest_008', subject: 'Backend Development', subject_id: 'cat_001', topic: 'System Design', topic_id: 'topic_004', question_text: 'Which of the following are valid strategies for scaling a relational database under heavy read load? (select all that apply)',
    status: 'ACTIVE', type: 'MCQ_MULTI', difficulty_level: 'HARD',
    options: [
      { id: 'qopt_011', option_text: 'Read replicas', is_correct: true },
      { id: 'qopt_012', option_text: 'Caching layer', is_correct: true },
      { id: 'qopt_013', option_text: 'Removing all indexes', is_correct: false },
      { id: 'qopt_014', option_text: 'Connection pooling', is_correct: true }
    ],
    created_at: '2026-06-20T08:35:00Z'
  }
];

// ---------------------------------------------------------------------------
// Papers
// ---------------------------------------------------------------------------

interface RawPaperSectionQuestion {
  question_id: string;
  question?: RawQuestion;
  weight?: number;
}

interface RawPaperSection {
  id: string;
  title: string;
  questions: RawPaperSectionQuestion[];
}

interface RawPaper {
  id: string;
  name: string;
  duration_minutes: number;
  use_weightage: boolean;
  total_marks?: number;
  sections: RawPaperSection[];
  created_at: string;
}

// Paper CRUD now hits the real exam backend (Template-as-Paper) — MOCK_PAPERS stays only as a
// fixture for the still-mocked exam-assignment/attempt/public-exam flow below.
const MOCK_PAPERS: RawPaper[] = [
  {
    id: 'paper_001', name: 'Backend Developer Assessment', duration_minutes: 60, use_weightage: true, total_marks: 25,
    sections: [
      { id: 'psec_001', title: 'Core Concepts', questions: [
        { question_id: 'quest_001', weight: 5 },
        { question_id: 'quest_002', weight: 10 }
      ] },
      { id: 'psec_002', title: 'Design & Architecture', questions: [
        { question_id: 'quest_003', weight: 10 }
      ] }
    ],
    created_at: '2026-06-21T09:00:00Z'
  },
  {
    id: 'paper_002', name: 'General Aptitude Test', duration_minutes: 30, use_weightage: false,
    sections: [
      { id: 'psec_003', title: 'Aptitude', questions: [
        { question_id: 'quest_004' },
        { question_id: 'quest_005' }
      ] }
    ],
    created_at: '2026-06-21T09:10:00Z'
  },
  {
    id: 'paper_003', name: 'QA & Testing Fundamentals', duration_minutes: 45, use_weightage: false,
    sections: [
      { id: 'psec_004', title: 'Testing Knowledge', questions: [
        { question_id: 'quest_006' },
        { question_id: 'quest_007' }
      ] }
    ],
    created_at: '2026-06-21T09:20:00Z'
  }
];

const populatePaperQuestions = (paper: RawPaper): RawPaper => ({
  ...paper,
  sections: paper.sections.map(section => ({
    ...section,
    questions: section.questions.map(q => ({ ...q, question: MOCK_QUESTIONS.find(qq => qq.id === q.question_id) }))
  }))
});

// ---------------------------------------------------------------------------
// Interview Rounds (TECHNICAL / HR / MANAGEMENT steps)
// ---------------------------------------------------------------------------

interface RawScheduledInterview {
  id: string;
  application_id: string;
  candidate_id?: string;
  step_id: string;
  scheduled_date: string;
  scheduled_time: string;
  location?: string;
  meeting_link?: string;
  interviewer_ids: string[];
  interviewer_names: string[];
}

interface RawInterviewerFeedback {
  id: string;
  interview_id: string;
  interviewer_id: string;
  interviewer_name: string;
  recommendation: 'REJECT' | 'CONSIDER' | 'HIRE' | 'STRONG_HIRE';
  feedback_text: string;
}

let interviewIdSeq = Date.now() + 3000;
let interviewerFeedbackIdSeq = Date.now() + 3500;
const nextInterviewId = () => `intv_${interviewIdSeq++}`;
const nextInterviewerFeedbackId = () => `ifb_${interviewerFeedbackIdSeq++}`;

let MOCK_SCHEDULED_INTERVIEWS: RawScheduledInterview[] = [];
let MOCK_INTERVIEWER_FEEDBACK: RawInterviewerFeedback[] = [];

// ---------------------------------------------------------------------------
// Exam Rounds (EXAMINATION steps)
// ---------------------------------------------------------------------------

interface RawExamAssignment {
  id: string;
  application_id: string;
  candidate_id?: string;
  step_id: string;
  scheduled_date: string;
  scheduled_time: string;
  paper_id: string;
  paper_name: string;
  deadline_date: string;
  examiner_ids: string[];
  examiner_names: string[];
  access_token: string;
}

interface RawExaminerFeedback {
  id: string;
  exam_assignment_id: string;
  examiner_id: string;
  examiner_name: string;
  score: number;
  recommendation: 'REJECT' | 'CONSIDER' | 'HIRE' | 'STRONG_HIRE';
  feedback_text: string;
}

let examAssignmentIdSeq = Date.now() + 4000;
let examinerFeedbackIdSeq = Date.now() + 4500;
let accessTokenSeq = Date.now() + 4250;
const nextExamAssignmentId = () => `exam_${examAssignmentIdSeq++}`;
const nextExaminerFeedbackId = () => `efb_${examinerFeedbackIdSeq++}`;
const nextAccessToken = () => `tok_${(accessTokenSeq++).toString(36)}${Math.random().toString(36).slice(2, 8)}`;

let MOCK_EXAMINER_FEEDBACK: RawExaminerFeedback[] = [];

// MOCK_EXAM_ASSIGNMENTS / MOCK_EXAM_ATTEMPTS are NOT plain in-memory arrays like the rest of
// this file's fixtures — they're read/written through localStorage on every access instead.
// Reason: the candidate's exam page (`/exam/:token`) is a completely separate, unauthenticated
// route that the candidate opens in their own tab/window. Since this whole "backend" is just
// JS running in the browser (no real server), a fresh tab gets a fresh, empty copy of any
// plain `let` array — the recruiter's assignment would never be visible there, and the
// candidate's submission would never be visible back in the recruiter's tab. localStorage is
// shared across ordinary tabs/windows of the same browser profile (NOT across incognito
// windows, which use a separate storage partition by design), which is enough to make the
// recruiter-assigns → candidate-submits → recruiter-grades loop actually testable locally.

const EXAM_ASSIGNMENTS_STORAGE_KEY = 'mock_recruitment_exam_assignments';
const EXAM_ATTEMPTS_STORAGE_KEY = 'mock_recruitment_exam_attempts';

function readFromStorage<T>(key: string): T[] {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T[]) : [];
  } catch {
    return [];
  }
}

function writeToStorage<T>(key: string, value: T[]): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // localStorage full/unavailable — falls back to this tab's in-memory copy for the rest
    // of the session, which is no worse than before this fix.
  }
}

function getExamAssignments(): RawExamAssignment[] {
  const list = readFromStorage<RawExamAssignment>(EXAM_ASSIGNMENTS_STORAGE_KEY);
  return list.filter(a => !(a.application_id === 'app_001' && a.step_id === 'step_001' && a.id.startsWith('exam_auto_')));
}

function setExamAssignments(list: RawExamAssignment[]): void {
  writeToStorage(EXAM_ASSIGNMENTS_STORAGE_KEY, list);
}

function getExamAttempts(): RawExamAttempt[] {
  return readFromStorage<RawExamAttempt>(EXAM_ATTEMPTS_STORAGE_KEY);
}

function setExamAttempts(list: RawExamAttempt[]): void {
  writeToStorage(EXAM_ATTEMPTS_STORAGE_KEY, list);
}

const SCHEDULED_INTERVIEWS_STORAGE_KEY = 'mock_recruitment_scheduled_interviews';
const INTERVIEWER_FEEDBACK_STORAGE_KEY = 'mock_recruitment_interviewer_feedback';

function getScheduledInterviews(): RawScheduledInterview[] {
  let list = readFromStorage<RawScheduledInterview>(SCHEDULED_INTERVIEWS_STORAGE_KEY);
  if (!list.some(i => i.application_id === 'cand-589499' || i.application_id === 'dcc4ae13-34bf-4492-af76-b93df02e9a6d')) {
    const seeded: RawScheduledInterview = {
      id: 'intv-316757',
      application_id: 'cand-589499',
      step_id: '476501dc-4d74-43a3-8589-c9f5dab66274',
      scheduled_date: '2026-06-29',
      scheduled_time: '05:55',
      location: 'zcxzc',
      meeting_link: 'cxzc',
      interviewer_ids: ['87c4eb4b-e642-48e8-97f0-240fdd64652c'],
      interviewer_names: ['Assigned Interviewer']
    };
    list = [seeded, ...list];
    writeToStorage(SCHEDULED_INTERVIEWS_STORAGE_KEY, list);
  }
  return list;
}

function setScheduledInterviews(list: RawScheduledInterview[]): void {
  writeToStorage(SCHEDULED_INTERVIEWS_STORAGE_KEY, list);
}

function getInterviewerFeedback(): RawInterviewerFeedback[] {
  return readFromStorage<RawInterviewerFeedback>(INTERVIEWER_FEEDBACK_STORAGE_KEY);
}

function setInterviewerFeedback(list: RawInterviewerFeedback[]): void {
  writeToStorage(INTERVIEWER_FEEDBACK_STORAGE_KEY, list);
}

// ---------------------------------------------------------------------------
// Exam Attempts — the candidate's own submission for an exam assignment,
// accessed unauthenticated via /exam/:token (token = the assignment's access_token).
// ---------------------------------------------------------------------------

interface RawExamAnswer {
  question_id: string;
  selected_option_ids?: string[];
  long_answer_text?: string;
  rating_value?: number;
  yes_no_value?: boolean;
}

interface RawExamQuestionResult {
  question_id: string;
  is_correct?: boolean;
  marks_awarded?: number;
}

type RawExamAttemptStatus = 'NOT_STARTED' | 'IN_PROGRESS' | 'SUBMITTED' | 'GRADED';

interface RawExamAttempt {
  id: string;
  exam_assignment_id: string;
  status: RawExamAttemptStatus;
  started_at?: string;
  submitted_at?: string;
  answers: RawExamAnswer[];
  question_results?: RawExamQuestionResult[];
  score_percentage?: number;
  total_marks_awarded?: number;
  graded_at?: string;
}

let examAttemptIdSeq = Date.now() + 4750;
const nextExamAttemptId = () => `attempt_${examAttemptIdSeq++}`;

/** Strips `is_correct`/`model_answers` before anything reaches the candidate's browser. */
function toPublicQuestion(q: RawQuestion): { id: string; type: RawQuestionType; question_text: string; options?: { id: string; option_text: string }[] } {
  return {
    id: q.id,
    type: q.type,
    question_text: q.question_text,
    options: q.options?.map(o => ({ id: o.id, option_text: o.option_text }))
  };
}

function buildPublicExamPaper(assignment: RawExamAssignment, paper: RawPaper, attempt: RawExamAttempt) {
  return {
    token: assignment.access_token,
    paper_name: paper.name,
    duration_minutes: paper.duration_minutes,
    deadline_date: assignment.deadline_date,
    attempt_status: attempt.status,
    sections: paper.sections.map(s => ({
      title: s.title,
      questions: s.questions
        .map(sq => MOCK_QUESTIONS.find(q => q.id === sq.question_id))
        .filter((q): q is RawQuestion => !!q)
        .map(toPublicQuestion)
    }))
  };
}

/** MCQ auto-grading at submit time — compares the candidate's selection against each option's
 *  `is_correct` flag. MCQ_MULTI is all-or-nothing: the selected set must exactly match the
 *  correct set for full credit, otherwise zero (no partial credit). Long Answer / Rating /
 *  Yes-No are left ungraded here — Long Answer needs the examiner, the others never score. */
function autoGradeMcq(paper: RawPaper, answers: RawExamAnswer[]): RawExamQuestionResult[] {
  const results: RawExamQuestionResult[] = [];
  for (const section of paper.sections) {
    for (const sq of section.questions) {
      const question = MOCK_QUESTIONS.find(q => q.id === sq.question_id);
      if (!question || (question.type !== 'MCQ_SINGLE' && question.type !== 'MCQ_MULTI')) continue;
      const answer = answers.find(a => a.question_id === sq.question_id);
      const correctIds = new Set((question.options ?? []).filter(o => o.is_correct).map(o => o.id));
      const selectedIds = new Set(answer?.selected_option_ids ?? []);
      const isCorrect = correctIds.size === selectedIds.size && [...correctIds].every(id => selectedIds.has(id));
      results.push({
        question_id: sq.question_id,
        is_correct: isCorrect,
        marks_awarded: paper.use_weightage ? (isCorrect ? (sq.weight ?? 0) : 0) : undefined
      });
    }
  }
  return results;
}

// ---------------------------------------------------------------------------
// Interceptor
// ---------------------------------------------------------------------------

export const mockRecruitmentApiInterceptor: HttpInterceptorFn = (req, next) => {
  const url = req.url;

  const ok = (data: unknown, message?: string, status = 200) => {
    console.log(`[MockRecruitmentApi] Request succeeded (${status}):`, data);
    return of(new HttpResponse({ status, body: message ? { status: 'Success', message, data } : { status: 'Success', data } }));
  };

  const fail = (status: number, code: string, message: string) =>
    throwError(() => new HttpErrorResponse({ status, url, error: { status: 'Error', code, message } }));

  // Candidate Registration form config (support both /forms/rec_candidate_form and /candidates/registration-config)
  if (req.method === 'GET' && (url.includes('/forms/rec_candidate_form') || url.includes('/recruitment/candidates/registration-config'))) {
    return ok(MOCK_CANDIDATE_REGISTRATION_CONFIG);
  }

  if (!url.includes('/recruitment/')) {
    return next(req);
  }

  console.log('[MockRecruitmentApi] Intercepting request:', req.method, url);

  // GET /recruitment/vacancies
  if (req.method === 'GET' && /\/recruitment\/vacancies(\?.*)?$/.test(url)) {
    return ok(MOCK_VACANCIES);
  }

  // GET /recruitment/vacancies/:id
  if (req.method === 'GET' && /\/recruitment\/vacancies\/[^/]+$/.test(url) && !url.includes('/applications')) {
    const vacancyId = url.split('/recruitment/vacancies/')[1].split('?')[0];
    const vacancy = MOCK_VACANCIES.find(v => v.id === vacancyId) ?? MOCK_VACANCIES[0];
    return ok(vacancy);
  }

  // POST /recruitment/vacancies
  if (req.method === 'POST' && /\/recruitment\/vacancies(\?.*)?$/.test(url)) {
    const body = req.body as any;
    const newVac: RawVacancy = {
      id: `vac_${Date.now()}`,
      name: body.name || 'New Vacancy',
      start_date: body.start_date || new Date().toISOString().slice(0, 10),
      end_date: body.end_date || new Date().toISOString().slice(0, 10),
      org_node_id: body.org_node_id || 'C001',
      org_node_name: 'RWB CA',
      job_title: body.job_title || body.name,
      job_description: body.job_description || '',
      salary_min: body.salary_min || 0,
      salary_max: body.salary_max || 0,
      salary_currency: body.salary_currency || 'LKR',
      location: body.location || 'Colombo',
      employment_type: body.employment_type || 'FULL_TIME',
      working_mode: body.working_mode || 'HYBRID',
      number_of_openings: body.number_of_openings || 1,
      recruiter_id: body.recruiter_id || '1',
      recruiter_name: 'Amal Bandara Perera',
      external_visibility: body.external_visibility ?? true,
      interview_steps: body.interview_steps || [
        { id: `step_${Date.now()}`, name: 'HR Round', type: 'HR', sequence_order: 1 }
      ],
      status: 'ACTIVE',
      closed_reason: null,
      positions_filled: 0,
      created_at: new Date().toISOString()
    };
    MOCK_VACANCIES = [newVac, ...MOCK_VACANCIES];
    return ok(newVac, 'Vacancy Created Successfully', 201);
  }

  // GET /recruitment/candidates
  if (req.method === 'GET' && /\/recruitment\/candidates(\?.*)?$/.test(url) && !url.includes('/registration-config')) {
    return ok(MOCK_CANDIDATES);
  }

  // GET /recruitment/candidates/:id
  if (req.method === 'GET' && /\/recruitment\/candidates\/[^/]+$/.test(url) && !url.includes('/registration-config')) {
    const candidateId = url.split('/recruitment/candidates/')[1].split('?')[0];
    const candidate = MOCK_CANDIDATES.find(c => c.id === candidateId) ?? MOCK_CANDIDATES[0];
    return ok(candidate);
  }

  // POST /recruitment/candidates
  if (req.method === 'POST' && /\/recruitment\/candidates(\?.*)?$/.test(url)) {
    let candidateData: any = {};
    if (typeof FormData !== 'undefined' && req.body instanceof FormData) {
      const dataStr = req.body.get('data') as string;
      if (dataStr) {
        try { candidateData = JSON.parse(dataStr); } catch { candidateData = {}; }
      }
      const cvFile = req.body.get('cv_file') as File;
      if (cvFile && typeof cvFile === 'object') {
        candidateData.cv_name = cvFile.name;
        candidateData.cv_url = 'https://example.com/mock-cv.pdf';
      }
    } else {
      candidateData = req.body || {};
    }

    const newCandidate = {
      id: `cand_${Date.now()}`,
      name: candidateData.full_name || candidateData.name || 'New Candidate',
      email: candidateData.email || '',
      phone: candidateData.phone || candidateData.contact_number || '',
      address: candidateData.address || '',
      notice_period_days: Number(candidateData.notice_period_days) || 0,
      current_salary_net: Number(candidateData.current_salary_net) || 0,
      current_salary_gross: Number(candidateData.current_salary_gross) || 0,
      current_salary_currency: candidateData.current_salary_currency || 'LKR',
      expected_salary_net: Number(candidateData.expected_salary_net) || 0,
      expected_salary_gross: Number(candidateData.expected_salary_gross) || 0,
      expected_salary_currency: candidateData.expected_salary_currency || 'LKR',
      experience_years: Number(candidateData.experience_years) || 0,
      education: candidateData.education || '',
      skills: Array.isArray(candidateData.skills) ? candidateData.skills : [],
      cv_url: candidateData.cv_url || 'https://example.com/mock-cv.pdf',
      cv_name: candidateData.cv_name || 'cv.pdf',
      created_at: new Date().toISOString(),
      vacancy_id: candidateData.vacancy_id || null,
      approval_status: 'APPLIED',
      current_step_id: null
    };
    MOCK_CANDIDATES = [newCandidate, ...MOCK_CANDIDATES];
    return ok(newCandidate, 'Candidate Created Successfully', 201);
  }

  // PUT /recruitment/candidates/:id
  if (req.method === 'PUT' && /\/recruitment\/candidates\/[^/]+$/.test(url)) {
    const candidateId = url.split('/recruitment/candidates/')[1].split('?')[0];
    const candidate = MOCK_CANDIDATES.find(c => c.id === candidateId);
    if (candidate) {
      Object.assign(candidate, req.body);
      return ok(candidate, 'Candidate Updated Successfully');
    }
    return ok(req.body);
  }


  // GET /recruitment/vacancies/:id/applications — interview/exam *scheduling* overlay only
  // now (status/currentStepId come from the real Candidate record on the frontend instead).
  // `step_hints` (JSON: [{candidateId, currentStepId}]) is required to know which of a
  // candidate's scheduled interviews/exam assignments — possibly several, across different
  // rounds — is the *current* one, since MOCK_APPLICATIONS (the old source for a candidate's
  // current_step_id) is never populated anymore: approve-step/reject-step are real now and
  // never touch any mock fixture.
  if (req.method === 'GET' && /\/recruitment\/vacancies\/[^/]+\/applications(\?.*)?$/.test(url)) {
    const vacancyId = url.split('/recruitment/vacancies/')[1].replace('/applications', '').split('?')[0];
    const stepHintsRaw = new URL(url, 'http://local').searchParams.get('step_hints');
    let stepHints: { candidateId: string; currentStepId: string | null }[] = [];
    try { stepHints = stepHintsRaw ? JSON.parse(stepHintsRaw) : []; } catch { stepHints = []; }

    const apps = stepHints.map(({ candidateId, currentStepId }) => {
      // Always match on both candidateId AND stepId — the fallback that ignored stepId was
      // causing the wrong step's interview to be reported (e.g. step A's scheduled interview
      // setting interview_scheduled:true for step B's row). When currentStepId is null the
      // candidate hasn't been approved yet, so they can't have a scheduled interview at all.
      const storedInterviews = getScheduledInterviews();
      const interview = currentStepId
        ? (storedInterviews.find(i => (i.application_id === candidateId || i.candidate_id === candidateId) && i.step_id === currentStepId) ?? null)
        : null;
      const assignment = currentStepId
        ? (getExamAssignments().find(ex => (ex.application_id === candidateId || ex.candidate_id === candidateId) && ex.step_id === currentStepId) ?? null)
        : null;
      const stub = MOCK_APPLICATIONS.find(a => a.id === candidateId);
      return {
        id: candidateId,
        vacancy_id: vacancyId,
        candidate_id: candidateId,
        candidate_name: stub?.candidate_name ?? '',
        candidate_email: stub?.candidate_email ?? '',
        candidate_phone: stub?.candidate_phone ?? '',
        status: stub?.status ?? 'APPLIED',
        current_step_id: currentStepId,
        created_inline: true,
        applied_date: stub?.applied_date ?? '',
        step_decisions: stub?.step_decisions ?? [],
        interview_scheduled: !!interview,
        interview_feedback_submitted: interview ? getInterviewerFeedback().some(f => f.interview_id === interview.id && f.recommendation) : false,
        exam_assigned: !!assignment,
        exam_feedback_submitted: assignment ? MOCK_EXAMINER_FEEDBACK.some(f => f.exam_assignment_id === assignment.id && f.recommendation) : false,
        interviewer_ids: interview ? interview.interviewer_ids : [],
        examiner_ids: assignment ? assignment.examiner_ids : []
      };
    });
    return ok(apps);
  }

  // DELETE /recruitment/vacancies/:id/applications/:applicationId — unlinks the candidate
  // from this vacancy only; the candidate record itself stays in Talent Pool. Lenient: a
  // real candidate (DL-033/Candidates-tab decision) may have no tracking stub yet — that's
  // a no-op success, not a 404, since there's nothing to unlink.
  if (req.method === 'DELETE' && /\/recruitment\/vacancies\/[^/]+\/applications\/[^/]+$/.test(url)) {
    const applicationId = url.split('/applications/')[1];
    MOCK_APPLICATIONS = MOCK_APPLICATIONS.filter(a => a.id !== applicationId);
    return ok(null, 'Candidate Successfully Removed From Vacancy');
  }

  // NOTE: Subject (criteria-categories), Topic, Question (criteria) and Paper (templates) CRUD
  // — formerly mocked here at /recruitment/questions, /categories, /topics, /papers — now hit the
  // real backend at `environment.examBaseUrl` (RecruitmentApiService no longer calls these mock
  // paths). The MOCK_QUESTIONS/MOCK_PAPERS fixtures and `populatePaperQuestions()` are kept below
  // — the still-mocked exam-assignment/attempt/public-exam flow (getAttemptForGrading/
  // getPublicExam/etc.) reads them directly.

  // GET /recruitment/applications/:id/steps/:stepId/interview
  if (req.method === 'GET' && /\/recruitment\/applications\/[^/]+\/steps\/[^/]+\/interview$/.test(url)) {
    const [applicationId, stepId] = url.split('/recruitment/applications/')[1].replace('/interview', '').split('/steps/');
    const interviews = getScheduledInterviews();
    const interview = interviews.find(i => (i.application_id === applicationId || i.candidate_id === applicationId) && i.step_id === stepId) ?? interviews.find(i => i.application_id === applicationId || i.candidate_id === applicationId) ?? null;
    const feedback = interview ? getInterviewerFeedback().filter(f => f.interview_id === interview.id) : [];
    return ok({ interview, feedback });
  }

  // POST /recruitment/applications/:id/steps/:stepId/interview — upsert
  if (req.method === 'POST' && /\/recruitment\/applications\/[^/]+\/steps\/[^/]+\/interview$/.test(url)) {
    const [applicationId, stepId] = url.split('/recruitment/applications/')[1].replace('/interview', '').split('/steps/');
    const body = req.body as Omit<RawScheduledInterview, 'id' | 'application_id' | 'step_id'>;
    const interviews = getScheduledInterviews();
    const existing = interviews.find(i => i.application_id === applicationId && i.step_id === stepId);
    const saved: RawScheduledInterview = { ...(existing ?? { id: nextInterviewId() }), ...body, application_id: applicationId, step_id: stepId } as RawScheduledInterview;
    setScheduledInterviews(existing
      ? interviews.map(i => i.id === existing.id ? saved : i)
      : [saved, ...interviews]);
    return ok([saved], 'Interview Successfully Scheduled', existing ? 200 : 201);
  }

  // DELETE /recruitment/applications/:id/steps/:stepId/interview — clears the display
  // mirror after a real cancel/delete call succeeds (see RecruitmentApiService).
  if (req.method === 'DELETE' && /\/recruitment\/applications\/[^/]+\/steps\/[^/]+\/interview$/.test(url)) {
    const [applicationId, stepId] = url.split('/recruitment/applications/')[1].replace('/interview', '').split('/steps/');
    const interviews = getScheduledInterviews();
    const existing = interviews.find(i => i.application_id === applicationId && i.step_id === stepId);
    setScheduledInterviews(interviews.filter(i => i.application_id !== applicationId || i.step_id !== stepId));
    if (existing) {
      setInterviewerFeedback(getInterviewerFeedback().filter(f => f.interview_id !== existing.id));
    }
    return ok(null, 'Interview Removed');
  }

  // POST /recruitment/interviews/:id/feedback — upsert per interviewer
  if (req.method === 'POST' && /\/recruitment\/interviews\/[^/]+\/feedback$/.test(url)) {
    const interviewId = url.split('/recruitment/interviews/')[1].replace('/feedback', '');
    const body = req.body as Omit<RawInterviewerFeedback, 'id' | 'interview_id'>;
    const feedbacks = getInterviewerFeedback();
    const existing = feedbacks.find(f => f.interview_id === interviewId && f.interviewer_id === body.interviewer_id);
    const saved: RawInterviewerFeedback = { ...(existing ?? { id: nextInterviewerFeedbackId() }), ...body, interview_id: interviewId } as RawInterviewerFeedback;
    setInterviewerFeedback(existing
      ? feedbacks.map(f => f.id === existing.id ? saved : f)
      : [saved, ...feedbacks]);
    return ok([saved], 'Feedback Successfully Submitted', existing ? 200 : 201);
  }

  // GET /recruitment/applications/:id/steps/:stepId/exam-assignment
  if (req.method === 'GET' && /\/recruitment\/applications\/[^/]+\/steps\/[^/]+\/exam-assignment$/.test(url)) {
    const [applicationId, stepId] = url.split('/recruitment/applications/')[1].replace('/exam-assignment', '').split('/steps/');
    const assignment = getExamAssignments().find(a => a.application_id === applicationId && a.step_id === stepId) ?? null;
    const feedback = assignment ? MOCK_EXAMINER_FEEDBACK.filter(f => f.exam_assignment_id === assignment.id) : [];
    return ok({ assignment, feedback });
  }

  // POST /recruitment/applications/:id/steps/:stepId/exam-assignment — upsert
  if (req.method === 'POST' && /\/recruitment\/applications\/[^/]+\/steps\/[^/]+\/exam-assignment$/.test(url)) {
    const [applicationId, stepId] = url.split('/recruitment/applications/')[1].replace('/exam-assignment', '').split('/steps/');
    const body = req.body as Omit<RawExamAssignment, 'id' | 'application_id' | 'step_id' | 'access_token'>;
    const assignments = getExamAssignments();
    const existing = assignments.find(a => a.application_id === applicationId && a.step_id === stepId);
    const saved: RawExamAssignment = {
      ...(existing ?? { id: nextExamAssignmentId(), access_token: nextAccessToken() }),
      ...body, application_id: applicationId, step_id: stepId
    } as RawExamAssignment;
    setExamAssignments(existing
      ? assignments.map(a => a.id === existing.id ? saved : a)
      : [saved, ...assignments]);

    // A fresh assignment also gets a fresh attempt shell — the candidate's exam doesn't exist
    // until the assignment does, and re-assigning a different paper resets any prior attempt.
    if (!existing || existing.paper_id !== saved.paper_id) {
      const attempts = getExamAttempts().filter(a => a.exam_assignment_id !== saved.id);
      setExamAttempts([{ id: nextExamAttemptId(), exam_assignment_id: saved.id, status: 'NOT_STARTED', answers: [] }, ...attempts]);
    }

    return ok([saved], 'Exam Successfully Assigned', existing ? 200 : 201);
  }

  // DELETE /recruitment/applications/:id/steps/:stepId/exam-assignment — clears the display
  // mirror (and its attempt/feedback) after a real cancel/delete call succeeds.
  if (req.method === 'DELETE' && /\/recruitment\/applications\/[^/]+\/steps\/[^/]+\/exam-assignment$/.test(url)) {
    const [applicationId, stepId] = url.split('/recruitment/applications/')[1].replace('/exam-assignment', '').split('/steps/');
    const assignments = getExamAssignments();
    const existing = assignments.find(a => a.application_id === applicationId && a.step_id === stepId);
    setExamAssignments(assignments.filter(a => a.application_id !== applicationId || a.step_id !== stepId));
    if (existing) {
      setExamAttempts(getExamAttempts().filter(a => a.exam_assignment_id !== existing.id));
      MOCK_EXAMINER_FEEDBACK = MOCK_EXAMINER_FEEDBACK.filter(f => f.exam_assignment_id !== existing.id);
    }
    return ok(null, 'Exam Assignment Removed');
  }

  // POST /recruitment/exam-assignments/:id/feedback — upsert per examiner
  if (req.method === 'POST' && /\/recruitment\/exam-assignments\/[^/]+\/feedback$/.test(url)) {
    const assignmentId = url.split('/recruitment/exam-assignments/')[1].replace('/feedback', '');
    const body = req.body as Omit<RawExaminerFeedback, 'id' | 'exam_assignment_id'>;
    const existing = MOCK_EXAMINER_FEEDBACK.find(f => f.exam_assignment_id === assignmentId && f.examiner_id === body.examiner_id);
    const saved: RawExaminerFeedback = { ...(existing ?? { id: nextExaminerFeedbackId() }), ...body, exam_assignment_id: assignmentId } as RawExaminerFeedback;
    MOCK_EXAMINER_FEEDBACK = existing
      ? MOCK_EXAMINER_FEEDBACK.map(f => f.id === existing.id ? saved : f)
      : [saved, ...MOCK_EXAMINER_FEEDBACK];
    return ok([saved], 'Feedback Successfully Submitted', existing ? 200 : 201);
  }

  // GET /recruitment/exam-assignments/:id/attempt — recruiter-side, full (unsanitized) Paper
  // + the candidate's attempt, for the Grade Attempt screen
  if (req.method === 'GET' && /\/recruitment\/exam-assignments\/[^/]+\/attempt$/.test(url)) {
    const assignmentId = url.split('/recruitment/exam-assignments/')[1].replace('/attempt', '');
    const assignment = getExamAssignments().find(a => a.id === assignmentId);
    if (!assignment) return fail(404, 'NOT_FOUND', 'Exam assignment not found.');
    const attempt = getExamAttempts().find(a => a.exam_assignment_id === assignmentId);
    if (!attempt) return fail(404, 'NOT_FOUND', 'Exam attempt not found.');
    const paper = MOCK_PAPERS.find(p => p.id === assignment.paper_id);
    if (!paper) return fail(404, 'NOT_FOUND', 'Paper not found.');
    return ok({ attempt, paper: populatePaperQuestions(paper) });
  }

  // PUT /recruitment/exam-attempts/:id/grade — examiner's shared grading pass. Merges manual
  // results (Long Answer, or a binary MCQ confirmation) into whatever auto-grading already ran
  // at submit time, then recomputes the total.
  if (req.method === 'PUT' && /\/recruitment\/exam-attempts\/[^/]+\/grade$/.test(url)) {
    const attemptId = url.split('/recruitment/exam-attempts/')[1].replace('/grade', '');
    const attempts = getExamAttempts();
    const existing = attempts.find(a => a.id === attemptId);
    if (!existing) return fail(404, 'NOT_FOUND', 'Exam attempt not found.');
    const assignment = getExamAssignments().find(a => a.id === existing.exam_assignment_id);
    const paper = assignment ? MOCK_PAPERS.find(p => p.id === assignment.paper_id) : undefined;
    if (!paper) return fail(404, 'NOT_FOUND', 'Paper not found.');

    const body = req.body as { question_results: RawExamQuestionResult[] };
    const mergedResults = [...(existing.question_results ?? [])];
    body.question_results.forEach(incoming => {
      const idx = mergedResults.findIndex(r => r.question_id === incoming.question_id);
      if (idx >= 0) mergedResults[idx] = incoming; else mergedResults.push(incoming);
    });

    // Rating/Yes-No never score — exclude them from the denominator, not just the numerator.
    const scorableQuestionIds = new Set(
      paper.sections.flatMap(s => s.questions.map(q => q.question_id))
        .filter(id => {
          const q = MOCK_QUESTIONS.find(mq => mq.id === id);
          return q && q.type !== 'RATING' && q.type !== 'YES_NO';
        })
    );
    const totalQuestions = scorableQuestionIds.size;
    const updated: RawExamAttempt = {
      ...existing,
      question_results: mergedResults,
      status: 'GRADED',
      graded_at: new Date().toISOString(),
      score_percentage: paper.use_weightage
        ? (paper.total_marks ? Math.round((mergedResults.reduce((sum, r) => sum + (r.marks_awarded ?? 0), 0) / paper.total_marks) * 100) : 0)
        : (totalQuestions ? Math.round((mergedResults.filter(r => scorableQuestionIds.has(r.question_id) && r.is_correct).length / totalQuestions) * 100) : 0),
      total_marks_awarded: paper.use_weightage ? mergedResults.reduce((sum, r) => sum + (r.marks_awarded ?? 0), 0) : undefined
    };
    setExamAttempts(attempts.map(a => a.id === attemptId ? updated : a));
    return ok([updated], 'Attempt Successfully Graded');
  }

  // GET /recruitment/public/exam/:token — candidate-facing, unauthenticated, sanitized
  if (req.method === 'GET' && /\/recruitment\/public\/exam\/[^/]+$/.test(url)) {
    const token = url.split('/recruitment/public/exam/')[1];
    let assignment = getExamAssignments().find(a => a.access_token === token);
    if (!assignment) {
      // Fallback for testing: auto-create a mock assignment if token isn't found.
      // We look if token matches any paper_id in MOCK_PAPERS, if so use that, otherwise default to first paper.
      const matchedPaper = MOCK_PAPERS.find(p => p.id === token) || MOCK_PAPERS[0];
      assignment = {
        id: `exam_auto_${token}`,
        application_id: 'app_dummy',
        step_id: 'step_dummy',
        scheduled_date: new Date().toISOString().slice(0, 10),
        scheduled_time: '10:00',
        paper_id: matchedPaper.id,
        paper_name: matchedPaper.name,
        deadline_date: new Date(Date.now() + 86400000).toISOString().slice(0, 10),
        examiner_ids: ['1'],
        examiner_names: ['Amal Bandara Perera'],
        access_token: token
      };
      setExamAssignments([assignment, ...getExamAssignments()]);
    }
    const paper = MOCK_PAPERS.find(p => p.id === assignment.paper_id);
    if (!paper) return fail(404, 'NOT_FOUND', 'Paper not found.');
    let attempt = getExamAttempts().find(a => a.exam_assignment_id === assignment.id);
    if (!attempt) {
      attempt = {
        id: `attempt_auto_${token}`,
        exam_assignment_id: assignment.id,
        status: 'NOT_STARTED',
        answers: []
      };
      setExamAttempts([attempt, ...getExamAttempts()]);
    }
    return ok(buildPublicExamPaper(assignment, paper, attempt));
  }

  // POST /recruitment/public/exam/:token/start — no-op once already started/submitted
  if (req.method === 'POST' && /\/recruitment\/public\/exam\/[^/]+\/start$/.test(url)) {
    const token = url.split('/recruitment/public/exam/')[1].replace('/start', '');
    let assignment = getExamAssignments().find(a => a.access_token === token);
    if (!assignment) {
      const matchedPaper = MOCK_PAPERS.find(p => p.id === token) || MOCK_PAPERS[0];
      assignment = {
        id: `exam_auto_${token}`,
        application_id: 'app_dummy',
        step_id: 'step_dummy',
        scheduled_date: new Date().toISOString().slice(0, 10),
        scheduled_time: '10:00',
        paper_id: matchedPaper.id,
        paper_name: matchedPaper.name,
        deadline_date: new Date(Date.now() + 86400000).toISOString().slice(0, 10),
        examiner_ids: ['1'],
        examiner_names: ['Amal Bandara Perera'],
        access_token: token
      };
      setExamAssignments([assignment, ...getExamAssignments()]);
    }
    const attempts = getExamAttempts();
    let attempt = attempts.find(a => a.exam_assignment_id === assignment.id);
    if (!attempt) {
      attempt = { id: `attempt_auto_${token}`, exam_assignment_id: assignment.id, status: 'NOT_STARTED', answers: [] };
      setExamAttempts([attempt, ...attempts]);
    }
    if (attempt.status === 'NOT_STARTED') {
      const updated: RawExamAttempt = { ...attempt, status: 'IN_PROGRESS', started_at: new Date().toISOString() };
      setExamAttempts(getExamAttempts().map(a => a.id === attempt.id ? updated : a));
    }
    return ok(null);
  }

  // POST /recruitment/public/exam/:token/submit — runs MCQ auto-grading immediately
  if (req.method === 'POST' && /\/recruitment\/public\/exam\/[^/]+\/submit$/.test(url)) {
    const token = url.split('/recruitment/public/exam/')[1].replace('/submit', '');
    let assignment = getExamAssignments().find(a => a.access_token === token);
    if (!assignment) {
      const matchedPaper = MOCK_PAPERS.find(p => p.id === token) || MOCK_PAPERS[0];
      assignment = {
        id: `exam_auto_${token}`,
        application_id: 'app_dummy',
        step_id: 'step_dummy',
        scheduled_date: new Date().toISOString().slice(0, 10),
        scheduled_time: '10:00',
        paper_id: matchedPaper.id,
        paper_name: matchedPaper.name,
        deadline_date: new Date(Date.now() + 86400000).toISOString().slice(0, 10),
        examiner_ids: ['1'],
        examiner_names: ['Amal Bandara Perera'],
        access_token: token
      };
      setExamAssignments([assignment, ...getExamAssignments()]);
    }
    const attempts = getExamAttempts();
    let attempt = attempts.find(a => a.exam_assignment_id === assignment.id);
    if (!attempt) {
      attempt = { id: `attempt_auto_${token}`, exam_assignment_id: assignment.id, status: 'NOT_STARTED', answers: [] };
      setExamAttempts([attempt, ...attempts]);
    }
    if (attempt.status === 'SUBMITTED' || attempt.status === 'GRADED') {
      return fail(409, 'ALREADY_SUBMITTED', 'This exam has already been submitted.');
    }
    const paper = MOCK_PAPERS.find(p => p.id === assignment.paper_id);
    if (!paper) return fail(404, 'NOT_FOUND', 'Paper not found.');

    const body = req.body as { answers: RawExamAnswer[] };
    const updated: RawExamAttempt = {
      ...attempt,
      answers: body.answers,
      question_results: autoGradeMcq(paper, body.answers),
      status: 'SUBMITTED',
      submitted_at: new Date().toISOString()
    };
    setExamAttempts(getExamAttempts().map(a => a.id === attempt.id ? updated : a));
    return ok(null);
  }

  return next(req);
};
